import { AxiosRequestConfig } from 'axios';
import { BaseApiClient } from '@core/api/BaseApiClient.ts';
import { ApiError, ApiResponse, IApiClient } from '@core/api/IApiClient';
import { generateId } from '@core/utils';

const mockFileIndex: Record<string, () => Promise<any>> = import.meta.glob('./data/**/*.json', { import: 'default' });

class MockApiClient extends BaseApiClient implements IApiClient {
    private static instance: MockApiClient | null = null;

    private mockErrors: Map<string, ApiError> = new Map();
    private delay: number = 100;

    constructor() {
        if (MockApiClient.instance) {
            return MockApiClient.instance;
        }

        super();
        MockApiClient.instance = this;

        if (typeof window !== 'undefined') {
            (window as any).__mockApiClient__ = this;

            // Load queued errors from init script
            const queuedErrors = (window as any).__mockApiErrors__;
            if (queuedErrors instanceof Map) {
                queuedErrors.forEach((error: ApiError, endpoint: string) => {
                    this.mockErrors.set(endpoint, error);
                });
            }
        }
    }

    // ============ HTTP Helpers Implementation ============

    protected async httpGet<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        await this.simulateDelay();

        // Handle arraybuffer response type (for images)
        if (config?.responseType === 'arraybuffer') {
            return this.getMockImageResponse<T>(url);
        }

        const response = await this.getMockResponse<T>(url, 'GET');
        // If no mock file found, return empty array
        return response ?? this.createResponse<T>([] as T, 200, 'OK');
    }

    protected async httpPost<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        await this.simulateDelay();

        const mockResponse = await this.getMockResponse<T>(url, 'POST');
        if (mockResponse) {
            return mockResponse;
        }

        // Fallback: return the posted data with generated fields
        return this.createResponse<T>(
            {
                ...data,
                id: generateId(),
                createdAt: new Date().toISOString()
            } as T,
            201,
            'Created'
        );
    }

    protected async httpPut<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        await this.simulateDelay();

        const mockResponse = await this.getMockResponse<T>(url, 'PUT');
        if (mockResponse) {
            return mockResponse;
        }

        // Fallback: return the updated data with timestamp
        return this.createResponse<T>(
            {
                ...data,
                updatedAt: new Date().toISOString()
            } as T,
            200,
            'OK'
        );
    }

    protected async httpPatch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        await this.simulateDelay();

        const mockResponse = await this.getMockResponse<T>(url, 'PATCH');
        if (mockResponse) {
            return mockResponse;
        }

        // Fallback: return the patched data with timestamp
        return this.createResponse<T>(
            {
                ...data,
                updatedAt: new Date().toISOString()
            } as T,
            200,
            'OK'
        );
    }

    protected async httpDelete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        await this.simulateDelay();

        const mockResponse = await this.getMockResponse<T>(url, 'DELETE');
        if (mockResponse) {
            return mockResponse;
        }

        // Fallback: return empty response with 204 status
        return this.createResponse<T>({} as T, 204, 'No Content');
    }

    // ============ Deprecated Generic Methods (for backward compatibility) ============
    /**
     * @deprecated Use domain-specific methods instead
     */
    async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        return this.httpGet<T>(url, config);
    }

    /**
     * @deprecated Use domain-specific methods instead
     */
    async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        return this.httpPost<T>(url, data, config);
    }

    /**
     * @deprecated Use domain-specific methods instead
     */
    async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        return this.httpPut<T>(url, data, config);
    }

    /**
     * @deprecated Use domain-specific methods instead
     */
    async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        return this.httpPatch<T>(url, data, config);
    }

    /**
     * @deprecated Use domain-specific methods instead
     */
    async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        return this.httpDelete<T>(url, config);
    }

    private async loadMockDataFromFile(
        url: string,
        method: string = 'GET'
    ): Promise<{ data: any; status: number } | null> {
        const pathWithoutQuery = url.split('?')[0];
        const segments = pathWithoutQuery.split('/').filter((s) => s.length > 0);

        if (segments.length === 0) {
            return null;
        }

        // Try from most specific path down to parent paths
        for (let i = segments.length; i > 0; i--) {
            const pathSegments = segments.slice(0, i);
            const group = pathSegments[0];
            const restPath = pathSegments.slice(1).join('_');
            const baseFileName = restPath ? `${restPath}_${method}` : `${group}_${method}`;

            const result = await this.findAndLoadMockFile(group, baseFileName);
            if (result !== null) {
                return result;
            }
        }

        return null;
    }

    private async findAndLoadMockFile(
        group: string,
        baseFileName: string
    ): Promise<{ data: any; status: number } | null> {
        const prefix = `./data/${group}/${baseFileName}_`;
        let successKey: string | null = null;
        let successStatus = 0;

        for (const key of Object.keys(mockFileIndex)) {
            if (!key.startsWith(prefix) || !key.endsWith('.json')) continue;

            const status = parseInt(key.slice(prefix.length, -5));
            if (Number.isNaN(status)) continue;

            if (status >= 400) {
                // Error file found — load it immediately (error wins over success)
                return { data: await mockFileIndex[key](), status };
            }

            if (successKey === null) {
                successKey = key;
                successStatus = status;
            }
        }

        if (successKey !== null) {
            return { data: await mockFileIndex[successKey](), status: successStatus };
        }

        // Fallback: legacy filename without status code
        const legacyKey = `./data/${group}/${baseFileName}.json`;
        if (mockFileIndex[legacyKey]) {
            return { data: await mockFileIndex[legacyKey](), status: 200 };
        }

        return null;
    }

    /**
     * Create an ApiError from mock data with error status code
     */
    private createApiErrorFromMockData(data: any, status: number): ApiError {
        const wrappedError: any = new Error(data.message || 'Mock error');
        wrappedError.response = {
            data: {
                message: data.message || 'Mock error',
                properties: data.properties
            },
            status: status,
            statusText: this.getStatusText(status)
        };
        wrappedError.status = status;
        return wrappedError;
    }

    private getStatusText(status: number): string {
        const statusTexts: Record<number, string> = {
            200: 'OK',
            201: 'Created',
            204: 'No Content',
            400: 'Bad Request',
            401: 'Unauthorized',
            403: 'Forbidden',
            404: 'Not Found',
            409: 'Conflict',
            500: 'Internal Server Error'
        };
        return statusTexts[status] || 'Unknown';
    }

    // ============ Mock Response Matching ============

    private async getMockImageResponse<T>(url: string): Promise<ApiResponse<T>> {
        const cleanUrl = this.normalizeUrl(url);

        // Extract imagePathUri from query parameters
        const urlObj = new URL(url, 'http://localhost');
        const imagePathUri = urlObj.searchParams.get('imagePathUri');

        if (!imagePathUri) {
            return this.createResponse<T>(new ArrayBuffer(0) as T, 200, 'OK');
        }

        // Load mock data file
        const result = await this.loadMockDataFromFile(cleanUrl, 'GET');

        if (result && result.data && Array.isArray(result.data)) {
            const data = result.data;
            // Find the matching image by imagePathUri
            const imageEntry = data.find((entry: any) => entry.imagePathUri === imagePathUri);

            if (imageEntry?.base64) {
                try {
                    // Convert base64 to ArrayBuffer
                    const base64Data = imageEntry.base64.includes(',')
                        ? imageEntry.base64.split(',')[1]
                        : imageEntry.base64;

                    const binaryString = atob(base64Data);
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }

                    return this.createResponse<T>(bytes.buffer as T, 200, 'OK');
                } catch (error) {
                    return this.createResponse<T>(new ArrayBuffer(0) as T, 404, 'Not Found');
                }
            }
        }

        return this.createResponse<T>(new ArrayBuffer(0) as T, 200, 'OK');
    }

    private async getMockResponse<T>(url: string, method: string): Promise<ApiResponse<T> | null> {
        const cleanUrl = this.normalizeUrl(url);

        // 1. Check for programmatically set mock errors first
        if (this.mockErrors.has(cleanUrl)) {
            throw this.mockErrors.get(cleanUrl)!;
        }

        // 2. Try loading from file
        const result = await this.loadMockDataFromFile(cleanUrl, method);

        if (result !== null) {
            const { data, status } = result;

            // Check if status code indicates an error
            if (status >= 400) {
                throw this.createApiErrorFromMockData(data, status);
            }

            return this.createResponse<T>(data, status, this.getStatusText(status));
        }

        // Return null when no file found (let caller handle fallback)
        return null;
    }

    private async simulateDelay(): Promise<void> {
        if (this.delay > 0) {
            await new Promise((resolve) => setTimeout(resolve, this.delay));
        }
    }

    // ============ Auth Methods ============

    setAuthToken(token: string): void {
        console.log('[MockApiClient] Auth token set (mock)');
    }

    setOrganisationId(orgId: string): void {
        console.log('[MockApiClient] Organisation ID set (mock):', orgId);
    }

    clearAuthToken(): void {
        console.log('[MockApiClient] Auth token cleared (mock)');
    }

    // ============ Testing Utilities ============

    setMockError(url: string, error: ApiError): void {
        const cleanUrl = this.normalizeUrl(url);
        this.mockErrors.set(cleanUrl, error);
    }

    async reset(): Promise<void> {
        this.mockErrors.clear();
    }
}

export default MockApiClient;
